import {
    formatDate,
    formatSignatureFieldValue,
    SignatureRequestId,
    validEmail,
} from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import {
    GetSignatureRequestQuery,
    SignatureFieldType,
    useGetSignatureRequestQuery,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { DocumentCallback, PageCallback } from "react-pdf/dist/cjs/shared/types";
import { DocumentRender, PdfDocumentProxyMetadata } from "../pdf/document-render";
import { compareSignatureTemplateFields } from "../signature-defaults";
import {
    getSignatureBorderStyle,
    getSignaturePlaceholderStyle,
} from "../signature-template/signature-colors";
import { SignatureFieldIcon } from "../signature-template/signature-field-icon";
import { SignatureRequestRecipients } from "./signature-request-recipients";
import { SignatureRequestStateText } from "./signature-request-state-text";

interface State {
    finishedInitialLoad: boolean;
    isLoading: boolean;
    numPages: number | null;
    pageNumber: number | null;
    currentPageDimensions: number[] | null;
    zoomLevel: number;
    signatureRequest: GetSignatureRequestQuery["getSignatureRequest"] | null;
}

const initialState: State = {
    finishedInitialLoad: false,
    isLoading: false,
    numPages: null,
    pageNumber: null,
    currentPageDimensions: null,
    zoomLevel: 1,
    signatureRequest: null,
};

type Action =
    | { type: "setPage"; page: number }
    | { type: "setZoomLevel"; zoomLevel: number }
    | {
          type: "onDocumentLoadSuccess";
          document: DocumentCallback;
          documentMetadata: PdfDocumentProxyMetadata;
      }
    | { type: "onPageLoadSuccess"; document: PageCallback }
    | {
          type: "initialLoadComplete";
          payload: GetSignatureRequestQuery["getSignatureRequest"];
      };

const reducer = (state: State, action: Action): State => {
    return produce(state, draft => {
        if (action.type === "setPage") {
            draft.pageNumber = action.page;
        }

        if (action.type === "setZoomLevel") {
            draft.zoomLevel = action.zoomLevel;
        }

        if (action.type === "onDocumentLoadSuccess") {
            draft.numPages = action.document.numPages;
            draft.pageNumber = 1;
        }
        if (
            action.type === "initialLoadComplete" &&
            !draft.finishedInitialLoad &&
            action.payload
        ) {
            draft.signatureRequest = action.payload;
            draft.finishedInitialLoad = true;
        }
    });
};

interface SignatureRequestExistingProps {
    signatureRequestId: SignatureRequestId;
    onRefetch(): void;
}

export const SignatureRequestExisting: React.FC<
    SignatureRequestExistingProps
> = props => {
    const { signatureRequestId, onRefetch } = props;

    const [state, dispatch] = React.useReducer(reducer, initialState);
    const query = useGetSignatureRequestQuery(
        { signatureRequestId },
        {
            onSuccess: data => {
                dispatch({
                    type: "initialLoadComplete",
                    payload: data.getSignatureRequest,
                });
            },
        },
    );
    const onLoadSuccess = React.useCallback(async (document: DocumentCallback) => {
        const documentMetadata = await document.getMetadata();
        dispatch({ type: "onDocumentLoadSuccess", document, documentMetadata });
    }, []);
    const onPageRender = React.useCallback((document: PageCallback) => {
        dispatch({ type: "onPageLoadSuccess", document });
    }, []);
    const setPage = React.useCallback((pageNumber: number) => {
        dispatch({ type: "setPage", page: pageNumber });
    }, []);
    const setZoomLevel = React.useCallback((zoomLevel: number) => {
        dispatch({ type: "setZoomLevel", zoomLevel });
    }, []);
    const refetch = React.useCallback(() => {
        query.refetch();
        onRefetch();
    }, [query, onRefetch]);

    const ccRecipients: string[] = React.useMemo(() => {
        const rawRecipients = state.signatureRequest?.ccRecipients || [];

        return rawRecipients
            .filter(r => validEmail(r.email))
            .map(r => {
                if (r.name && r.name !== r.email) {
                    return `${r.name} <${r.email}>`;
                }

                return r.email;
            });
    }, [state.signatureRequest?.ccRecipients]);

    const fieldsForPage = (state.signatureRequest?.signatureRequestFields || [])
        .filter(f => f.page === state.pageNumber)
        .sort(compareSignatureTemplateFields);

    return (
        <QueryRenderer query={query} name="SignatureRequestExisting.GetSignatureRequest">
            {data => (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <div>
                            <p>Template</p>
                            <p className="font-bold">
                                {data.getSignatureRequest.filename}
                            </p>
                        </div>
                        <div>
                            <p>Status</p>
                            <p className="font-bold">
                                <SignatureRequestStateText
                                    state={data.getSignatureRequest.state}
                                />
                            </p>
                        </div>
                        <div>
                            <p>Sent</p>
                            <p className="font-bold">
                                {formatDate(data.getSignatureRequest.createdAt)}
                            </p>
                        </div>
                        <div>
                            <p>Recipients</p>
                            <SignatureRequestRecipients
                                request={data.getSignatureRequest}
                                onRefetch={refetch}
                            />
                        </div>
                        {ccRecipients.length > 0 ? (
                            <div>
                                <p>CC Recipients</p>
                                <p className="font-bold">{ccRecipients.join(", ")}</p>
                            </div>
                        ) : null}
                    </div>
                    <div>
                        <p>Document preview</p>
                        <DocumentRender
                            documentUrl={`/api/files/download/${data.getSignatureRequest.signatureTemplateFileId}`}
                            pageNumber={state.pageNumber}
                            setPageNumber={setPage}
                            onRenderSuccess={onPageRender}
                            onLoadSuccess={onLoadSuccess}
                            setZoomLevel={setZoomLevel}>
                            {fieldsForPage.map(f => (
                                <SentSignatureRequestField
                                    field={f}
                                    key={f.id}
                                    zoomLevel={state.zoomLevel}
                                />
                            ))}
                        </DocumentRender>
                    </div>
                </div>
            )}
        </QueryRenderer>
    );
};

interface SentSignatureRequestFieldProps {
    field: GetSignatureRequestQuery["getSignatureRequest"]["signatureRequestFields"][number];
    zoomLevel: number;
}

export const SentSignatureRequestField: React.FC<
    SentSignatureRequestFieldProps
> = props => {
    const { field, zoomLevel } = props;
    const text = formatSignatureFieldValue(field) || field.placeholder;

    if (text) {
        return (
            <FilledSentSignatureRequestField
                field={field}
                zoomLevel={zoomLevel}
                text={text}
            />
        );
    }

    return <BlankSentSignatureRequestField field={field} zoomLevel={zoomLevel} />;
};

interface FilledSentSignatureRequestFieldProps extends SentSignatureRequestFieldProps {
    text: string;
}

const FilledSentSignatureRequestField: React.FC<
    FilledSentSignatureRequestFieldProps
> = props => {
    const { field, zoomLevel, text } = props;
    const fieldStyles: React.CSSProperties = {
        left: field.x * zoomLevel,
        top: field.y * zoomLevel,
        width: field.width * zoomLevel,
        minHeight: field.height * zoomLevel,
        fontFamily: field.font,
        fontSize: field.fontSize * zoomLevel,
        lineHeight: `${(field.height - 1) * zoomLevel}px`,
    };
    const fieldClasses = `absolute rounded-sm border-2 whitespace-pre-wrap break-words ${getSignaturePlaceholderStyle(
        field.recipientIndex,
    )} ${getSignatureBorderStyle(field.recipientIndex)} ${
        field.fieldType === SignatureFieldType.Checkbox ? "text-center" : ""
    }`;

    return (
        <div className={fieldClasses} style={fieldStyles}>
            {text}
        </div>
    );
};

const BlankSentSignatureRequestField: React.FC<
    SentSignatureRequestFieldProps
> = props => {
    const { field, zoomLevel } = props;
    const fieldStyles: React.CSSProperties = {
        left: field.x * zoomLevel,
        top: field.y * zoomLevel,
        width: field.width * zoomLevel,
        height: field.height * zoomLevel,
        fontSize: field.fontSize * zoomLevel,
        lineHeight: `${(field.height - 1) * zoomLevel}px`,
    };
    const fieldClasses = `absolute rounded-sm p-1 border-2 flex items-center space-x-2 ${getSignaturePlaceholderStyle(
        field.recipientIndex,
    )} ${getSignatureBorderStyle(field.recipientIndex)} ${
        field.fieldType === SignatureFieldType.Checkbox ? "justify-center" : ""
    }`;

    return (
        <div className={fieldClasses} style={fieldStyles}>
            <SignatureFieldIcon
                fieldType={field.fieldType}
                recipientIndex={field.recipientIndex}
            />
            <p>{field.fieldType}</p>
        </div>
    );
};
